import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayCard } from './DayCard'
import { RecipePicker } from './RecipePicker'
import { ServingEditor } from './ServingEditor'
import { useMealPlan } from './useMealPlan'
import { useAssignMealSlot } from './useAssignMealSlot'
import { useUpdateServingMultiplier } from './useUpdateServingMultiplier'
import { useRemoveMealSlot } from './useRemoveMealSlot'
import { useSuggestMealSlot } from './useSuggestMealSlot'
import { useApproveSuggestion } from './useApproveSuggestion'
import { useDismissSuggestion } from './useDismissSuggestion'
import { useUserRole } from '@/shared/hooks/useUserRole'
import { useMealPlanRealtime } from './useMealPlanRealtime'
import { getMondayOfWeek, DAY_NAMES } from './utils'
import type { MealSlot } from './types'

function navigateWeek(weekStartDate: string, direction: 1 | -1): string {
  const date = new Date(weekStartDate + 'T00:00:00')
  date.setDate(date.getDate() + direction * 7)
  return getMondayOfWeek(date)
}

export function MealPlanView() {
  const { t } = useTranslation('meal-plan')
  const [weekStartDate, setWeekStartDate] = useState(() => getMondayOfWeek())
  const [pickerOpen, setPickerOpen] = useState(false)
  const [activeDayOfWeek, setActiveDayOfWeek] = useState<number | null>(null)
  const [servingEditorOpen, setServingEditorOpen] = useState(false)
  const [editingSlot, setEditingSlot] = useState<MealSlot | null>(null)

  useMealPlanRealtime()

  // Compute today's dayOfWeek (0=Monday...6=Sunday) for highlighting
  const todayDayOfWeek = useMemo(() => {
    const jsDay = new Date().getDay() // 0=Sun, 1=Mon...6=Sat
    return jsDay === 0 ? 6 : jsDay - 1 // Convert to 0=Mon...6=Sun
  }, [])
  const isCurrentWeek = weekStartDate === getMondayOfWeek()

  const { data: mealPlan, isLoading, isError } = useMealPlan(weekStartDate)
  const userRole = useUserRole()
  const assignMutation = useAssignMealSlot(weekStartDate)
  const servingMutation = useUpdateServingMultiplier(weekStartDate)
  const removeMutation = useRemoveMealSlot(weekStartDate)
  const suggestMutation = useSuggestMealSlot(weekStartDate)
  const approveMutation = useApproveSuggestion(weekStartDate)
  const dismissMutation = useDismissSuggestion(weekStartDate)

  const handleSlotTap = (dayOfWeek: number) => {
    setActiveDayOfWeek(dayOfWeek)
    setPickerOpen(true)
  }

  const handleRecipeSelect = (recipeId: string) => {
    if (activeDayOfWeek === null) return
    if (userRole === 'Member') {
      suggestMutation.mutate({ weekStartDate, dayOfWeek: activeDayOfWeek, recipeId })
    } else {
      assignMutation.mutate({ weekStartDate, dayOfWeek: activeDayOfWeek, recipeId })
    }
    setPickerOpen(false)
  }

  const handleEditServing = (slot: MealSlot) => {
    setEditingSlot(slot)
    setServingEditorOpen(true)
  }

  const handleSaveServing = (multiplier: number) => {
    if (!editingSlot) return
    servingMutation.mutate({ weekStartDate, dayOfWeek: editingSlot.dayOfWeek, servingMultiplier: multiplier })
    setServingEditorOpen(false)
    setEditingSlot(null)
  }

  const handleRemoveSlot = (dayOfWeek: number) => {
    removeMutation.mutate({ weekStartDate, dayOfWeek })
  }

  const handleApprove = (dayOfWeek: number) => {
    approveMutation.mutate({ weekStartDate, dayOfWeek })
  }

  const handleDismiss = (dayOfWeek: number) => {
    dismissMutation.mutate({ weekStartDate, dayOfWeek })
  }

  // Format week range label (e.g. "Mon 10 Mar – Sun 16 Mar")
  const weekEndDate = (() => {
    const d = new Date(weekStartDate + 'T00:00:00')
    d.setDate(d.getDate() + 6)
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  })()
  const weekStartLabel = new Date(weekStartDate + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  })

  return (
    <div className="flex flex-col px-4 pt-4 pb-24">
      {/* Week navigation header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          aria-label={t('nav.previousWeek')}
          onClick={() => setWeekStartDate(navigateWeek(weekStartDate, -1))}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ChevronLeft className="h-5 w-5 text-ink" aria-hidden="true" />
        </button>
        <p className="text-sm font-semibold text-ink">
          {t('weekLabel', { start: weekStartLabel, end: weekEndDate })}
        </p>
        <button
          type="button"
          aria-label={t('nav.nextWeek')}
          onClick={() => setWeekStartDate(navigateWeek(weekStartDate, 1))}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ChevronRight className="h-5 w-5 text-ink" aria-hidden="true" />
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div role="status" aria-label="Loading meal plan" className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-line border-t-accent" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <p className="text-danger text-sm py-4" role="alert">
          {t('error.loadFailed')}
        </p>
      )}

      {/* 7-day grid — vertical single-column on mobile, 7-column grid at md */}
      {mealPlan && (
        <div className="flex flex-col gap-3 md:grid md:grid-cols-7 md:gap-4">
          {mealPlan.slots.map((slot) => {
            const isPlanner = userRole === 'Planner' || userRole === null
            const isMember = userRole === 'Member'
            const hasConfirmedRecipe = !!slot.recipeId && !slot.isSuggestion

            return (
              <DayCard
                key={slot.dayOfWeek}
                slot={slot}
                dayName={DAY_NAMES[slot.dayOfWeek] ?? ''}
                userRole={userRole}
                isToday={isCurrentWeek && slot.dayOfWeek === todayDayOfWeek}
                onSlotTap={
                  isMember && hasConfirmedRecipe
                    ? () => {} // AC5: Members cannot tap confirmed slots
                    : () => handleSlotTap(slot.dayOfWeek)
                }
                onEditServing={isPlanner && hasConfirmedRecipe ? () => handleEditServing(slot) : undefined}
                onRemove={isPlanner && hasConfirmedRecipe ? () => handleRemoveSlot(slot.dayOfWeek) : undefined}
                onApprove={slot.isSuggestion ? () => handleApprove(slot.dayOfWeek) : undefined}
                onDismiss={slot.isSuggestion ? () => handleDismiss(slot.dayOfWeek) : undefined}
              />
            )
          })}
        </div>
      )}

      {/* Recipe picker bottom sheet */}
      <RecipePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleRecipeSelect}
      />

      {/* Serving editor bottom sheet */}
      {servingEditorOpen && editingSlot && (
        <ServingEditor
          currentMultiplier={editingSlot.servingMultiplier}
          recipeServingSize={editingSlot.servingSize ?? 1}
          onSave={handleSaveServing}
          onClose={() => {
            setServingEditorOpen(false)
            setEditingSlot(null)
          }}
        />
      )}
    </div>
  )
}
